import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import * as Sentry from '@sentry/angular';
import { ApplicationService } from '@/app/application.service';

export interface SupportContext {
  pageUrlAtOpen: string;
  pageUrlAtSubmit: string;
  appVersion: string;
  userAgent: string;
  screen: string;
  viewport: string;
  devicePixelRatio: number;
  language: string;
  timeZone: string;
  online: boolean;
  colorScheme: 'light' | 'dark';
  sentryReplayId?: string;
  sentryEventId?: string;
}

/**
 * Pagina-URL's van de app bevatten geen persoonsgegevens (querystrings dragen alleen
 * navigatie-id's), dus de URL blijft leesbaar. Enige uitzondering: een `token`-parameter
 * (wachtwoordreset, e-mailverificatie) wordt gemaskeerd, in pad en hash-deel.
 */
export function maskUrlSecrets(url: string): string {
  return url.replace(/([?&]token=)[^&#\s'"]*/gi, '$1[masked]');
}

/** Technische context die met een melding meegaat; nooit chart- of persoonsgegevens. */
@Injectable({ providedIn: 'root' })
export class SupportContextService {
  private readonly appService = inject(ApplicationService);
  private readonly transloco = inject(TranslocoService);

  private pageUrlAtOpen = '';

  snapshotOnOpen(): void {
    this.pageUrlAtOpen = maskUrlSecrets(location.href);
  }

  /** Schermresolutie in fysieke pixels, als `W × H`. */
  screenSize(): string {
    const ratio = this.pixelRatio();
    return `${Math.round(window.screen.width * ratio)} × ${Math.round(window.screen.height * ratio)}`;
  }

  /** Viewport in CSS-pixels, als `W × H`. */
  viewportSize(): string {
    return `${window.innerWidth} × ${window.innerHeight}`;
  }

  collect(): SupportContext {
    return {
      pageUrlAtOpen: this.pageUrlAtOpen,
      pageUrlAtSubmit: maskUrlSecrets(location.href),
      appVersion: this.appService.version,
      userAgent: navigator.userAgent,
      screen: this.screenSize(),
      viewport: this.viewportSize(),
      devicePixelRatio: this.pixelRatio(),
      language: this.transloco.getActiveLang(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine,
      colorScheme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      sentryReplayId: this.sentryReplayId(),
      sentryEventId: this.sentryEventId(),
    };
  }

  private pixelRatio(): number {
    return window.devicePixelRatio || 1;
  }

  private sentryReplayId(): string | undefined {
    try {
      return Sentry.getReplay()?.getReplayId() || undefined;
    } catch {
      return undefined;
    }
  }

  private sentryEventId(): string | undefined {
    try {
      return Sentry.lastEventId() || undefined;
    } catch {
      return undefined;
    }
  }
}
